import moment from 'moment';
import { useMemo } from 'react';

import type { Student } from '../../../api/types/student';
import type { Subscription, SubscriptionDetails } from '../../../api/types/subscription';
import { useIsHubAuthenticated } from '../../app-shell/useIsHubAuthenticated';
import api from '../../legacy/api';

type SubscriptionLabel = 'active' | 'try-for-free' | 'expired' | 'cancelled' | { kind: 'trial-ending'; days: number };

export type StudentSubscriptionLabel = {
  text: string;
  className: string;
  buttonText: string;
  buttonClassName: string;
};

// An active Bundled or Cluey+ subscription wins, including when its paid-until date is null.
// Otherwise the furthest paid-until date wins. A null date is valid and does not beat a real date.
// When paid-until dates match, a non-Trial subscription wins over a Trial.
// If every date is null, the first non-Trial subscription is used, or the first subscription if all are Trials.
const getLatestSubscription = (subscriptions: SubscriptionDetails[]): SubscriptionDetails | null => {
  if (subscriptions.length === 0) {
    return null;
  }

  const priority = subscriptions.filter(
    (subscription) =>
      subscription.subscriptionStatus === 'Active' &&
      (subscription.subscriptionType === 'Bundled' || subscription.subscriptionType === 'Cluey+')
  );
  const candidates = priority.length > 0 ? priority : subscriptions;
  const dated = candidates.filter((subscription) => subscription.subscriptionPaidUntilDate);
  const pool = dated.length > 0 ? dated : candidates;

  return pool.reduce((furthest, current) => {
    const currentIsLater =
      !!current.subscriptionPaidUntilDate &&
      !!furthest.subscriptionPaidUntilDate &&
      moment(current.subscriptionPaidUntilDate).isAfter(moment(furthest.subscriptionPaidUntilDate));
    if (currentIsLater) {
      return current;
    }

    const samePaidUntilDate = current.subscriptionPaidUntilDate === furthest.subscriptionPaidUntilDate;
    if (samePaidUntilDate && furthest.subscriptionType === 'Trial' && current.subscriptionType !== 'Trial') {
      return current;
    }

    return furthest;
  });
};

const getSubscriptionLabel = (subscriptions: SubscriptionDetails[] | undefined): SubscriptionLabel | null => {
  if (!subscriptions || subscriptions.length === 0) {
    return 'try-for-free';
  }

  const latest = getLatestSubscription(subscriptions);
  if (!latest) {
    return 'try-for-free';
  }

  if (latest.subscriptionType === 'Trial' && latest.subscriptionPaidUntilDate) {
    const paidUntil = moment(latest.subscriptionPaidUntilDate);
    if (paidUntil.isBefore(moment(), 'day')) {
      return 'expired';
    }
    const days = paidUntil.clone().startOf('day').diff(moment().startOf('day'), 'days') + 1;
    return { kind: 'trial-ending', days };
  }

  if (['Bundled', 'Cluey+'].includes(latest.subscriptionType) && latest.subscriptionStatus === 'Cancelled') {
    const paidUntil = moment(latest.subscriptionPaidUntilDate);
    if (paidUntil.isBefore(moment(), 'day')) {
      return 'cancelled';
    }
  }

  return 'active';
};

const subscriptionLabelText = (label: SubscriptionLabel) => {
  if (label === 'try-for-free') {
    return 'Try for free';
  }
  if (label === 'expired') {
    return 'Expired';
  }
  if (label === 'cancelled') {
    return 'Cancelled';
  }
  if (label === 'active') {
    return '';
  }
  return `Expire in ${label.days}d`;
};

const subscriptionBgClassName = (label: SubscriptionLabel) => {
  if (label === 'try-for-free') {
    return 'bg-green-300';
  }
  if (label === 'cancelled') {
    return 'bg-yellow-400';
  }
  if (label === 'expired') {
    return 'bg-brown-300';
  }
  if (label === 'active') {
    return '';
  }
  return 'bg-[#FFCA9D]';
};

const subscriptionButtonText = (label: SubscriptionLabel) => {
  if (label === 'active' || (typeof label !== 'string' && label.kind === 'trial-ending')) {
    return 'Explore Cluey+';
  }
  return 'Cluey+';
};

const subscriptionButtonClassName = (label: SubscriptionLabel) => {
  if (['try-for-free', 'cancelled', 'expired'].includes(label as string)) {
    return 'border-2 border-dashed border-grey-200';
  }
  return 'border border-solid border-grey-400';
};

const buildLabelsByStudentId = (
  students: Student[],
  subscriptionsData: Subscription[] | undefined
): Record<string, StudentSubscriptionLabel> => {
  const labels: Record<string, StudentSubscriptionLabel> = {};

  for (const student of students) {
    const record = subscriptionsData?.find((subscription) => subscription.studentId === student.studentId);
    const label = getSubscriptionLabel(record?.subscriptions);
    if (label) {
      labels[student.studentId] = {
        text: subscriptionLabelText(label),
        className: subscriptionBgClassName(label),
        buttonText: subscriptionButtonText(label),
        buttonClassName: subscriptionButtonClassName(label),
      };
    }
  }

  return labels;
};

// Matches each student to their latest subscription and returns the label text and background class.
const useStudentSubscriptionLabel = (students: Student[]) => {
  const isHubAuthenticated = useIsHubAuthenticated();
  const { data: subscriptionsData } = api.subscription.getAll.useQuery(undefined, {
    enabled: isHubAuthenticated,
  });

  const labelsByStudentId = useMemo(
    () => buildLabelsByStudentId(students, subscriptionsData),
    [students, subscriptionsData]
  );

  const getStudentSubscriptionLabel = (studentId: string): StudentSubscriptionLabel | null =>
    labelsByStudentId[studentId] ?? null;

  return { getStudentSubscriptionLabel };
};

export default useStudentSubscriptionLabel;
